"use client";

import { Wifi, WifiOff } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface ConnectionFooterProps {
  /** Live network reachability (from the outbox). */
  online: boolean;
  /** Whether the "Powered by Wexio" badge is allowed (plan-gated, shell-side). */
  showBranding: boolean;
}

/** How long the "back online" line lingers after coming back online. */
const RECONNECT_LINGER_MS = 3000;

/**
 * The widget's bottom line. Normally the "Powered by Wexio" badge, but the
 * CONNECTION STATUS takes over the same slot when it matters — so a single
 * strip at the very bottom of the panel serves every tab:
 *
 *   - offline          → a persistent "you're offline" line (danger).
 *   - just reconnected → a "back online" line (success) for ~3 s, then reverts.
 *   - otherwise        → the branding badge (when allowed), else nothing.
 *
 * The status shows even when branding is hidden (white-label). All three states
 * render inside ONE fixed-height slot with an opacity-only crossfade (absolute
 * layers), so swapping between them never changes the footer's height — earlier
 * the badge and the status strip had different heights and the panel's bottom
 * edge visibly jumped on each transition.
 */
export function ConnectionFooter({ online, showBranding }: ConnectionFooterProps) {
  const t = useTranslations("messages");
  const tBrand = useTranslations("branding");
  const [showReconnected, setShowReconnected] = useState(false);
  const wasOnlineRef = useRef(online);

  useEffect(() => {
    const was = wasOnlineRef.current;
    wasOnlineRef.current = online;
    if (!online) {
      setShowReconnected(false);
      return;
    }
    if (!was && online) {
      setShowReconnected(true);
      const timer = setTimeout(() => setShowReconnected(false), RECONNECT_LINGER_MS);
      return () => clearTimeout(timer);
    }
  }, [online]);

  const view: "offline" | "online" | "branding" | null = !online
    ? "offline"
    : showReconnected
      ? "online"
      : showBranding
        ? "branding"
        : null;

  // Nothing to show (online + branding hidden) → keep a zero-height slot so the
  // layout is identical whether or not a status is present.
  const layer =
    "absolute inset-0 flex items-center justify-center gap-1.5 px-4 text-[10px] font-medium";
  return (
    <div className={cn("relative shrink-0", view ? "h-[26px]" : "h-0")}>
      <AnimatePresence initial={false} mode="wait">
        {view === "offline" && (
          <motion.div
            key="offline"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(layer, "bg-wx-danger/10 text-wx-danger")}
          >
            <WifiOff aria-hidden="true" className="size-3" />
            <span>{t("offlineBanner")}</span>
          </motion.div>
        )}
        {view === "online" && (
          <motion.div
            key="online"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(layer, "bg-wx-success/10 text-wx-success")}
          >
            <Wifi aria-hidden="true" className="size-3" />
            <span>{t("backOnline")}</span>
          </motion.div>
        )}
        {view === "branding" && (
          <motion.div
            key="branding"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={cn(layer, "bg-wx-bg text-wx-fg-subtle")}
          >
            <span>{tBrand("poweredBy")}</span>
            <a
              href="https://wexio.io?ref=widget"
              target="_blank"
              rel="noopener"
              className="font-semibold text-wx-fg-muted transition-colors hover:text-wx-fg"
            >
              {tBrand("wexio")}
            </a>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
